import type { CrmTranslate } from './crm-copy';

export function createCrmContactListCopy(t: CrmTranslate) {
  return {
    contactsPage: {
      documentTitle: t('crm.contactsPage.documentTitle'),
      documentTitleForCompany: (company: string) =>
        t('crm.contactsPage.documentTitleForCompany', {
          values: { company },
        }),
      title: t('crm.contactsPage.title'),
      counts: {
        contacts: (count: number) =>
          t('crm.contactsPage.counts.contacts', {
            values: { count },
          }),
      },
      actions: {
        clearFilter: t('crm.contactsPage.actions.clearFilter'),
        grid: t('crm.contactsPage.actions.grid'),
        list: t('crm.contactsPage.actions.list'),
        importCsv: t('crm.contactsPage.actions.importCsv'),
        addContact: t('crm.contactsPage.actions.addContact'),
        addContactNoPermission: t('crm.contactsPage.actions.addContactNoPermission'),
        clearSearch: t('crm.contactsPage.actions.clearSearch'),
        selectAllContactsTitle: t('crm.contactsPage.actions.selectAllContactsTitle'),
        unselectAllContactsTitle: t('crm.contactsPage.actions.unselectAllContactsTitle'),
        selectAll: t('crm.contactsPage.actions.selectAll'),
        unselectAll: t('crm.contactsPage.actions.unselectAll'),
        advancedFilters: t('crm.contactsPage.actions.advancedFilters'),
        removeSearchFilter: t('crm.contactsPage.actions.removeSearchFilter'),
        removeCompanyFilter: t('crm.contactsPage.actions.removeCompanyFilter'),
        removeAdvancedFilter: (name: string) =>
          t('crm.contactsPage.actions.removeAdvancedFilter', {
            values: { name },
          }),
        clearAllFilters: t('crm.contactsPage.actions.clearAllFilters'),
      },
      search: {
        placeholder: t('crm.contactsPage.search.placeholder'),
      },
      sort: {
        label: t('crm.contactsPage.sort.label'),
        name: t('crm.contactsPage.sort.name'),
        email: t('crm.contactsPage.sort.email'),
        company: t('crm.contactsPage.sort.company'),
        created: t('crm.contactsPage.sort.created'),
        updated: t('crm.contactsPage.sort.updated'),
        sortOrderTitle: (order: string) =>
          t('crm.contactsPage.sort.sortOrderTitle', {
            values: { order },
          }),
        ascending: t('crm.contactsPage.sort.ascending'),
        descending: t('crm.contactsPage.sort.descending'),
      },
      pageSize: {
        show: t('crm.contactsPage.pageSize.show'),
        perPage: t('crm.contactsPage.pageSize.perPage'),
      },
      filters: {
        filteredBy: (company: string) =>
          t('crm.contactsPage.filters.filteredBy', {
            values: { company },
          }),
        activeFilters: t('crm.contactsPage.filters.activeFilters'),
        searchTag: (query: string) =>
          t('crm.contactsPage.filters.searchTag', {
            values: { query },
          }),
        companyTag: (company: string) =>
          t('crm.contactsPage.filters.companyTag', {
            values: { company },
          }),
        advancedTag: (name: string, value: string) =>
          t('crm.contactsPage.filters.advancedTag', {
            values: { name, value },
          }),
        yes: t('crm.contactsPage.filters.yes'),
        no: t('crm.contactsPage.filters.no'),
      },
      advancedFilterLabels: {
        job_title: t('crm.contactsPage.advancedFilterLabels.jobTitle'),
        lead_status: t('crm.contactsPage.advancedFilterLabels.leadStatus'),
        company_id: t('crm.contactsPage.advancedFilterLabels.company'),
        employment_role: t('crm.contactsPage.advancedFilterLabels.role'),
        seniority: t('crm.contactsPage.advancedFilterLabels.seniority'),
        lifecycle_stage: t('crm.contactsPage.advancedFilterLabels.stage'),
        city: t('crm.contactsPage.advancedFilterLabels.city'),
        state: t('crm.contactsPage.advancedFilterLabels.state'),
        country: t('crm.contactsPage.advancedFilterLabels.country'),
        has_email: t('crm.contactsPage.advancedFilterLabels.hasEmail'),
        has_phone: t('crm.contactsPage.advancedFilterLabels.hasPhone'),
        has_mobile: t('crm.contactsPage.advancedFilterLabels.hasMobile'),
        min_score: t('crm.contactsPage.advancedFilterLabels.minScore'),
        max_score: t('crm.contactsPage.advancedFilterLabels.maxScore'),
        created_after: t('crm.contactsPage.advancedFilterLabels.createdAfter'),
        created_before: t('crm.contactsPage.advancedFilterLabels.createdBefore'),
        updated_after: t('crm.contactsPage.advancedFilterLabels.updatedAfter'),
        updated_before: t('crm.contactsPage.advancedFilterLabels.updatedBefore'),
        tags: t('crm.contactsPage.advancedFilterLabels.tags'),
      },
      states: {
        errorTitle: t('crm.contactsPage.states.errorTitle'),
        errorDescription: t('crm.contactsPage.states.errorDescription'),
      },
    },
    contactsList: {
      fallbacks: {
        unnamedContact: t('crm.contactsList.fallbacks.unnamedContact'),
      },
      nouns: {
        contact: t('crm.contactsList.nouns.contact'),
        contacts: t('crm.contactsList.nouns.contacts'),
      },
      counts: {
        selected: (selected: number, total: number) =>
          t('crm.contactsList.counts.selected', {
            values: { selected, total },
          }),
        showing: (visible: number, total: number) =>
          t('crm.contactsList.counts.showing', {
            values: { visible, total },
          }),
      },
      actions: {
        confirm: t('crm.contactsList.actions.confirm'),
        cancel: t('crm.contactsList.actions.cancel'),
        delete: t('crm.contactsList.actions.delete'),
        deleting: t('crm.contactsList.actions.deleting'),
        undo: t('crm.contactsList.actions.undo'),
        selectAll: t('crm.contactsList.actions.selectAll'),
        unselectAll: t('crm.contactsList.actions.unselectAll'),
        exitSelectionMode: t('crm.contactsList.actions.exitSelectionMode'),
        addToSalesPipelineTitle: t('crm.contactsList.actions.addToSalesPipelineTitle'),
        addToSalesPipelineAria: t('crm.contactsList.actions.addToSalesPipelineAria'),
        pipeline: t('crm.contactsList.actions.pipeline'),
      },
      states: {
        emptyTitle: t('crm.contactsList.states.emptyTitle'),
        emptySearchDescription: t('crm.contactsList.states.emptySearchDescription'),
        emptyCreateTitle: t('crm.contactsList.states.emptyCreateTitle'),
        emptyCreateDescription: t('crm.contactsList.states.emptyCreateDescription'),
      },
      confirmations: {
        deleteSelectedTitle: t('crm.contactsList.confirmations.deleteSelectedTitle'),
        deleteSelectedMessage: (count: number, noun: string) =>
          t('crm.contactsList.confirmations.deleteSelectedMessage', {
            values: { count, noun },
          }),
      },
      messages: {
        alreadyInPipeline: (name: string, stage: string) =>
          t('crm.contactsList.messages.alreadyInPipeline', {
            values: { name, stage },
          }),
        addedToStage: (name: string, stage: string) =>
          t('crm.contactsList.messages.addedToStage', {
            values: { name, stage },
          }),
        deleted: (count: number, noun: string) =>
          t('crm.contactsList.messages.deleted', {
            values: { count, noun },
          }),
        restored: (count: number, noun: string) =>
          t('crm.contactsList.messages.restored', {
            values: { count, noun },
          }),
        deleteFailed: (count: number, noun: string) =>
          t('crm.contactsList.messages.deleteFailed', {
            values: { count, noun },
          }),
      },
      errors: {
        noPipelinesAvailable: t('crm.contactsList.errors.noPipelinesAvailable'),
        noPipelineFound: t('crm.contactsList.errors.noPipelineFound'),
        noFirstStageFound: t('crm.contactsList.errors.noFirstStageFound'),
        failedAddContactToPipeline: t('crm.contactsList.errors.failedAddContactToPipeline'),
        noStagesFoundInPipeline: t('crm.contactsList.errors.noStagesFoundInPipeline'),
        errorAddingContactToPipeline: t('crm.contactsList.errors.errorAddingContactToPipeline'),
        failedDeleteContact: t('crm.contactsList.errors.failedDeleteContact'),
        failedRestoreContacts: t('crm.contactsList.errors.failedRestoreContacts'),
        errorDeletingContacts: t('crm.contactsList.errors.errorDeletingContacts'),
      },
      pipeline: {
        inPipelineTitle: (stage: string) =>
          t('crm.contactsList.pipeline.inPipelineTitle', {
            values: { stage },
          }),
        addingToPipeline: t('crm.contactsList.pipeline.addingToPipeline'),
      },
    },
  };
}
